import React, { useRef, useEffect, useState, useCallback } from 'react';
import styles from './Chart.module.css';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AuscultationIcon } from '../../../components/icons/Icons';
import { useSelector } from 'react-redux';
import VitalCardHeader from './components/VitalCardHeader';
import { getLatestMeasurement } from '../../../utils/unitUtils';

const AuscultationCardView = React.forwardRef(({ className, auscData, healthEvaluationData, vitalName, handlePointClick }, ref) => {
  const chartContainerRef = useRef(null);

  const { startDate, endDate } = useSelector((state) => state.patientDetailFilter);
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState({});
  const vitals = useSelector((state) => state.vital.modalities[vitalName]);
  const [opacity, setOpacity] = useState({});

  const updateData = useCallback(() => {
    // Group data by date and deviceName
    const groupedData = auscData.reduce((acc, entry) => {
      const date = entry.date;
      if (!acc[date]) {
        acc[date] = { date };
      }
      acc[date][entry.deviceName] = entry;
      return acc;
    }, {});

    let transformedData = Object.values(groupedData);
    transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
    setData(transformedData);
    const deviceOpacity = auscData.reduce((acc, entry) => {
      const { deviceName } = entry;

      if (!acc[deviceName]) {
        acc[deviceName] = [];
      }
      acc[deviceName].push(entry);

      return acc;
    }, {});

    setOpacity(Object.keys(deviceOpacity).reduce((acc, key) => {
      acc[key] = 1;
      return acc;
    }, {}));
    const latestMeasurementData = getLatestMeasurement(
      auscData,
      vitalName,
      false,
      ' br/min'
    );
    setDisplayData(latestMeasurementData);
  }, [auscData]);

  useEffect(() => {
    updateData();
  }, [updateData, startDate, endDate]);

  const handleLegendClick = (e) => {
    const deviceName = e.value;
    setOpacity((op) => ({ ...op, [deviceName]: op[deviceName] === 1 ? 0 : 1 }));
  };

  const deviceNames = Array.from(new Set(auscData.map(entry => entry.deviceName)));

  const deviceNamesData = deviceNames.filter(deviceName =>
    auscData.some(entry => entry.deviceName === deviceName && entry.measurement !== null)
  );

  const colors = {
    0: '#7A6B78',
    1: '#00162E',
  };
  
  const AuscTooltip = ({ active, payload, label, vitals }) => {
    if (active && payload && payload.length) {
      const dataKey = payload[0].dataKey.split('.');
      const deviceName = dataKey[0];
      const data = payload[0].payload[deviceName] ?? payload[0].payload;
      
      // Find matching data from healthEvaluationData to get posture and activity level
      const matchingHealthData = healthEvaluationData.find(entry => 
        entry.deviceName === deviceName && 
        entry.timeStamp === data.timeStamp
      );
      
      return (
        <div className={styles.PerinTooltip}>
          <p style={{ fontSize:"16px"}}>{`${deviceName}: ${data["date"] ?? 'Date: N/A'}`}</p>
          {matchingHealthData?.posture && (
            <p style={{ fontSize:"16px"}}>{`Posture: ${matchingHealthData.posture}`}</p>
          )}
          {matchingHealthData?.activityLevel && (
            <p style={{ fontSize:"16px"}}>{`Activity Level: ${matchingHealthData.activityLevel}`}</p>
          )}
        </div>
      );
    }
  
    return null;
  };

  return (
    <div className={`${styles.cardContainer} ${className}`} ref={chartContainerRef}>
      <VitalCardHeader
        Icon={AuscultationIcon}
        vitalName={vitals.modalityName}
        measurement={displayData.measurement}
        latestDate={displayData.latestDate}
      />
      <div className={styles.chartWrapper} ref={ref}>
        <ResponsiveContainer width={"100%"} height={150}>
        <LineChart type="monotone" data={data} margin={{ bottom: 5 }}>
          <XAxis dataKey="date" fontSize={12} />
          <YAxis
            domain={0}
            tick={false}
          />
          <Tooltip content={<AuscTooltip vitals={vitals} />} />
          <Legend
            verticalAlign="top"
            height={30}
            align={"right"}
            iconType="square"
            wrapperStyle={{ color: 'red', fontSize: '14px', fontWeight: 'bold' }}
            payload={deviceNamesData.map((deviceName, index) => ({
              value: deviceName,
              type: 'square',
              color: colors[index] || '#000000',
            }))}
            onClick={handleLegendClick}
          />
          {deviceNamesData.map((deviceName, index) => (
            <Line 
              connectNulls
              key={deviceName}
              type="linear"
              dataKey={deviceName + '.measurement'}
              stroke='#828282'
              strokeOpacity={opacity[deviceName] || 0}
              strokeWidth={3}
              dot={(props) => {
                const { cx, cy, value, key } = props;
                if (value !== undefined && value !== null) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4}
                      fill='white'
                      stroke={'#828282'}
                      strokeOpacity={opacity[deviceName] || 0}
                      strokeWidth={2}
                      key={key}
                      style={{ pointerEvents: 'visible' }}
                    />
                  );
                } else {
                  return null;
                }
              }}
              activeDot={(props) => {
                const { cx, cy, value, key, payload } = props;
                const dotColor = '#828282';
                if (value !== undefined && value !== null) {
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={4}
                      onClick={(e) => {
                        e.stopPropagation(); // Ensure the event doesn't bubble up
                        handlePointClick(deviceName, payload[deviceName], {...vitals, modalityType: 'ausc'});
                      }}
                      style={{ pointerEvents: 'visible' }}
                      fill={dotColor === '#828282' ? `rgba(130, 130, 130, ${opacity[deviceName]})` : 'none'}
                      stroke={dotColor}
                      strokeOpacity={opacity[deviceName] || 0}
                      strokeWidth={2}
                      key={key}
                    />
                  );
                } else {
                  return null;
                }
              }}
            />
          ))}
        </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export default AuscultationCardView;
